// React Imports
import { useRef } from 'react'

// Next Imports
import Link from 'next/link'
import { useParams } from 'next/navigation'

// Third-party Imports
import PerfectScrollbar from 'react-perfect-scrollbar'

// Type Imports
import type { ChildrenType } from '@core/types'
import type { Locale } from '@configs/i18n'

// Component Imports
import NavHeader from '@menu/components/vertical-menu/NavHeader'
import Logo from '@components/layout/shared/Logo'
import NavCollapseIcons from '@menu/components/vertical-menu/NavCollapseIcons'

// Hook Imports
import useHorizontalNav from '@menu/hooks/useHorizontalNav'

// Util Imports
import { mapHorizontalToVerticalMenu } from '@menu/utils/menuUtils'
import { getLocalizedUrl } from '@/utils/i18n'

// Create styled component using Tailwind classes instead of MUI styled
const BoxShadow = ({ className, ref }: { className?: string; ref: React.RefObject<HTMLDivElement> }) => (
  <div
    ref={ref}
    className={`absolute top-[60px] -left-2 z-[2] opacity-0 pointer-events-none w-[calc(100%+15px)] h-16 transition-opacity duration-150 ease-in-out bg-gradient-to-b from-background via-background/85 to-transparent ${className}`}
  />
)

interface ScrollWrapperProps {
  children: React.ReactNode
  onScroll?: (container: any) => void
  options?: {
    wheelPropagation: boolean
    suppressScrollX: boolean
  }
  onScrollY?: (container: any) => void
  className?: string
}

const VerticalNavContent = ({ children }: ChildrenType) => {
  // Hooks
  const { isBreakpointReached } = useHorizontalNav()
  const { lang: locale } = useParams()

  // Refs
  const shadowRef = useRef<HTMLDivElement>(null)

  // Define ScrollWrapper component based on breakpoint
  const ScrollWrapper = ({ children, ...props }: ScrollWrapperProps) => {
    if (isBreakpointReached) {
      return <div {...props}>{children}</div>
    }

    return <PerfectScrollbar {...props}>{children}</PerfectScrollbar>
  }

  const scrollMenu = (container: any, isPerfectScrollbar: boolean) => {
    const scrollContainer = isBreakpointReached || !isPerfectScrollbar ? container.target : container

    if (shadowRef.current) {
      if (scrollContainer.scrollTop > 0) {
        if (!shadowRef.current.classList.contains('scrolled')) {
          shadowRef.current.classList.add('scrolled')
        }
      } else {
        shadowRef.current.classList.remove('scrolled')
      }
    }
  }

  const navIcons = {
    locked: <i className='ri-radio-button-line text-xl' />,
    unlocked: <i className='ri-checkbox-blank-circle-line text-xl' />,
    close: <i className='ri-close-line text-xl' />
  }

  return (
    <>
      <NavHeader>
        <Link href={getLocalizedUrl('/', locale as Locale)}>
          <Logo />
        </Link>
        <NavCollapseIcons
          lockedIcon={navIcons.locked}
          unlockedIcon={navIcons.unlocked}
          closeIcon={navIcons.close}
          className='text-textSecondary'
        />
      </NavHeader>
      <BoxShadow ref={shadowRef} />
      <ScrollWrapper
        {...(isBreakpointReached
          ? {
              className: 'h-full overflow-y-auto overflow-x-hidden',
              onScroll: container => scrollMenu(container, false)
            }
          : {
              options: { wheelPropagation: false, suppressScrollX: true },
              onScrollY: container => scrollMenu(container, true)
            })}
      >
        {mapHorizontalToVerticalMenu(children)}
      </ScrollWrapper>
    </>
  )
}

export default VerticalNavContent
